import React, { createContext, useContext, useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSubmissions from "@/pages/admin/AdminSubmissions";
import AdminSubmissionDetail from "@/pages/admin/AdminSubmissionDetail";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminRecovery from "@/pages/admin/AdminRecovery";
import ArtistDashboard from "@/pages/artist/ArtistDashboard";
import ArtistSubmitStep1 from "@/pages/artist/ArtistSubmitStep1";
import ArtistSubmitStep2 from "@/pages/artist/ArtistSubmitStep2";
import ArtistSubmissionDetail from "@/pages/artist/ArtistSubmissionDetail";
import ArtistRecovery from "@/pages/artist/ArtistRecovery";
import BrandDashboard from "@/pages/brand/BrandDashboard";
import BrandSubmissions from "@/pages/brand/BrandSubmissions";
import BrandSubmissionDetail from "@/pages/brand/BrandSubmissionDetail";
import BrandAnalytics from "@/pages/brand/BrandAnalytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Token verification failed:", error);
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const getDefaultRoute = (role) => {
    switch (role) {
      case "admin": return "/admin";
      case "artist": return "/artist";
      case "brand": return "/brand";
      default: return "/login";
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAdmin: user?.role === "admin",
    isArtist: user?.role === "artist",
    isBrand: user?.role === "brand",
    getDefaultRoute
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading, getDefaultRoute } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to={getDefaultRoute(user.role)} replace />;
  }

  return children;
};

// App Content with Routes
const AppContent = () => {
  const { user, loading, getDefaultRoute } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-white text-xl font-heading">LOADING...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={getDefaultRoute(user.role)} replace /> : <LoginPage />} />
      
      {/* Admin (Agency) Routes */}
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/submissions" element={<ProtectedRoute requiredRole="admin"><AdminSubmissions /></ProtectedRoute>} />
      <Route path="/admin/submissions/:id" element={<ProtectedRoute requiredRole="admin"><AdminSubmissionDetail /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute requiredRole="admin"><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute requiredRole="admin"><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/recovery" element={<ProtectedRoute requiredRole="admin"><AdminRecovery /></ProtectedRoute>} />
      
      {/* Artist Routes */}
      <Route path="/artist" element={<ProtectedRoute requiredRole="artist"><ArtistDashboard /></ProtectedRoute>} />
      <Route path="/artist/submit" element={<ProtectedRoute requiredRole="artist"><ArtistSubmitStep1 /></ProtectedRoute>} />
      <Route path="/artist/submit/:id/media" element={<ProtectedRoute requiredRole="artist"><ArtistSubmitStep2 /></ProtectedRoute>} />
      <Route path="/artist/submission/:id" element={<ProtectedRoute requiredRole="artist"><ArtistSubmissionDetail /></ProtectedRoute>} />
      <Route path="/artist/submission/:id/edit" element={<ProtectedRoute requiredRole="artist"><ArtistSubmitStep1 /></ProtectedRoute>} />
      <Route path="/artist/recovery" element={<ProtectedRoute requiredRole="artist"><ArtistRecovery /></ProtectedRoute>} />
      
      {/* Brand Routes */}
      <Route path="/brand" element={<ProtectedRoute requiredRole="brand"><BrandDashboard /></ProtectedRoute>} />
      <Route path="/brand/analytics" element={<ProtectedRoute requiredRole="brand"><BrandAnalytics /></ProtectedRoute>} />
      <Route path="/brand/submissions" element={<ProtectedRoute requiredRole="brand"><BrandSubmissions /></ProtectedRoute>} />
      <Route path="/brand/submissions/:id" element={<ProtectedRoute requiredRole="brand"><BrandSubmissionDetail /></ProtectedRoute>} />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
